import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@engyalo/design-system';
import { useFeatureFlag, flag } from '../../../../hooks/useFeatureFlag';
import { useChatStore } from '../../../../redux/reducers/chatReducer';
import { isWamid } from '../../../../views/home/Chat/hooks/utils';
import { MessageType } from '../../Message';
import { ReplyActionWrapper } from './styled';

export const MESSAGE_AGE_LIMIT_MS = 30 * 24 * 60 * 60 * 1000;

interface ReplyActionProps {
  /** The full message object to set as the reply target. */
  message: MessageType;
}

/**
 * Checks whether a message can be used as a reply target.
 * A message is replyable when its `id` carries a WhatsApp message id (starts
 * with "wamid.") and is less than 30 days old.
 */
export const getIsReplyable = (id: string | undefined, timestamp: string | undefined): boolean => {
  const ts = Number(timestamp);
  const replyabilityChecks = [
    typeof id === 'string' && isWamid(id),
    !Number.isNaN(ts),
    Date.now() - ts < MESSAGE_AGE_LIMIT_MS,
  ];

  return replyabilityChecks.every(Boolean);
};

/**
 * Context action rendered on a chat message bubble that allows agents to
 * reply to a specific message.  Gated behind the `enable_contextual_replies`
 * Split.io feature flag.  Returns `null` when the flag is off.
 */
const ReplyAction: React.FC<ReplyActionProps> = ({ message }) => {
  const { t } = useTranslation();
  const isReplyEnabled = useFeatureFlag(flag.ENABLE_CONTEXTUAL_REPLIES);
  const { setReplyingToMessage } = useChatStore();

  if (!isReplyEnabled) {
    return null;
  }

  const chatId = message.chat?.id;
  const chatTimestamp = message.chat?.timestamp;
  const isReplyable = getIsReplyable(chatId, chatTimestamp);

  const handleReply = () => {
    setReplyingToMessage(message);
  };

  return (
    <ReplyActionWrapper className="messageOptions">
      <Tooltip title={!isReplyable ? t('messages.replyDisabledTooltip') : ''} placement="top">
        <span>
          <IconButton
            data-testid="replyAction"
            onClick={handleReply}
            disabled={!isReplyable}
            variant="text"
            color="neutral2"
            size="small"
            aria-label={t('messages.reply')}
          >
            <i className="fa-regular fa-reply" />
          </IconButton>
        </span>
      </Tooltip>
    </ReplyActionWrapper>
  );
};

export default ReplyAction;
